import React, { createContext, useContext, useEffect, useState } from 'react';
import { MsalProvider, useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { msalInstance, loginRequest } from '../config/msalConfig';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const getAccessToken = async (instance, customScope = null) => {
  if (typeof customScope === 'object' && Array.isArray(customScope)) {
    customScope = customScope[0]; // Unwrap if accidentally array-wrapped
  }
  const accounts = instance.getAllAccounts();
  if (!accounts || accounts.length === 0) {
    throw new Error('No user account found');
  }
 
  const tokenRequest = customScope
    ? { scopes: [customScope] }
    : { ...loginRequest };
 
  try {
    const response = await instance.acquireTokenSilent({
      ...tokenRequest,
      account: accounts[0],
    });
    return response.accessToken;
  } catch (error) {
    const response = await instance.acquireTokenPopup(tokenRequest);
    return response.accessToken;
  }
};

const AuthProviderComponent = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (inProgress === 'none' && accounts.length > 0) {
      const account = accounts[0];
      setUser({
        username: account.username,
        name: account.name,
        homeAccountId: account.homeAccountId,
      });
      setLoading(false);
    } else if (inProgress === 'none' && accounts.length === 0) {
      setUser(null);
      setLoading(false);
    }
  }, [accounts, inProgress]);

  const login = async () => {
    try {
      await instance.loginPopup(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await instance.logoutPopup();
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  };

  // Wrapper that uses the component’s instance/accounts
  const getAccessTokenWrapper = async (customScope = null) => {
    return getAccessToken(instance, customScope);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    getAccessToken: getAccessTokenWrapper,
    isAuthenticated: accounts.length > 0,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const AuthProvider = ({ children }) => (
  <MsalProvider instance={msalInstance}>
    <AuthProviderComponent>
      {children}
    </AuthProviderComponent>
  </MsalProvider>
);

export const AuthenticatedRoute = ({ children }) => (
  <>
    <AuthenticatedTemplate>
      {children}
    </AuthenticatedTemplate>
    <UnauthenticatedTemplate>
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Please log in to access this feature</h2>
        <button onClick={() => msalInstance.loginPopup(loginRequest)}>
          Login
        </button>
      </div>
    </UnauthenticatedTemplate>
  </>
);